import { observer } from 'mobx-react'
import { QRCodeSVG } from 'qrcode.react'
import { useContext, useEffect, useRef, useState } from 'react'
import { useInterval } from 'usehooks-ts'
import { Button, Header, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { devLog } from '@/methods/devLog'
import { forceRedirect } from '@/methods/forceRedirect'
import { usePageVisibility } from '@/hooks/usePageVisibility'
import { API_URL, READID_SERVICE_PATH } from '@/constants/apiUrl'

const POLL_INTERVAL_MS = 3000

type ReadIdStatus = 'pending' | 'processing' | 'completed'

interface GetStatusResponse {
  isSessionStarted: boolean
  status: ReadIdStatus
  redirectUrl?: string
}

interface StartSessionResponse {
  qrCode: string
  url: string
}

export const ReadIdPage = observer(() => {
  const store = useContext(StoreContext)
  const { readIdPage: trans } = store.TranslationsState.translations

  const [status, setStatus] = useState<ReadIdStatus>('pending')
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  const [shouldPoll, setShouldPoll] = useState(true)
  const [isStartingSession, setIsStartingSession] = useState(false)

  // Synchronous re-entrancy lock: state updates are async, so a ref guards
  // against concurrent start-session calls (which would create multiple sessions).
  const isStartingSessionRef = useRef(false)

  const isVisible = usePageVisibility()

  const startSession = async (): Promise<void> => {
    if (isStartingSessionRef.current) return

    isStartingSessionRef.current = true
    setIsStartingSession(true)

    try {
      const { data } = await axiosInstance.post<StartSessionResponse>(
        `${API_URL}/${READID_SERVICE_PATH}/start-session`,
        {},
        { withCredentials: true }
      )

      setQrCodeUrl(data.url)
    } catch (e) {
      devLog(e)
    } finally {
      isStartingSessionRef.current = false
      setIsStartingSession(false)
    }
  }

  const checkStatus = async (): Promise<void> => {
    try {
      const { data } = await axiosInstance.get<GetStatusResponse>(
        `${API_URL}/${READID_SERVICE_PATH}/get-status`,
        { withCredentials: true }
      )

      setStatus(data.status)

      if (data.status === 'completed' && data.redirectUrl) {
        setShouldPoll(false)
        forceRedirect(data.redirectUrl)
        return
      }

      // Auto-start a session only when one is genuinely pending and not yet started.
      // This avoids starting a new session on page refresh when the flow is already
      // in progress (e.g. status is processing).
      if (data.status === 'pending' && !data.isSessionStarted && !qrCodeUrl) {
        await startSession()
      }
    } catch (e) {
      devLog(e)
    }
  }

  useEffect(() => {
    setShouldPoll(isVisible)
  }, [isVisible])

  useEffect(() => {
    checkStatus()
  }, [])

  useInterval(checkStatus, shouldPoll && status !== 'completed' ? POLL_INTERVAL_MS : null)

  const isProcessing = status === 'processing'
  const showQrCode = !!qrCodeUrl && !isProcessing

  if (isProcessing) {
    return (
      <div className="page-container mobile-m-0">
        <Header>{trans?.header}</Header>

        <div className="generic__paragraph-centered">{trans?.processingDescription}</div>

        <LoadingSpinner className="loading-spinner--small-padding" />
      </div>
    )
  }

  return (
    <div className="page-container mobile-m-0">
      <Header>{trans?.header}</Header>

      <div className="generic__paragraph-centered m-0-0-30">
        {showQrCode ? trans?.scanQrDescription : trans?.generateNewQrCodeDescription}
      </div>

      {showQrCode ? (
        <div className="generic__paragraph-centered">
          <QRCodeSVG
            value={qrCodeUrl}
            href={qrCodeUrl}
            data-testid="readid-qr-code"
            role="img"
            aria-label={trans?.qrCodeAlt}
            title={trans?.qrCodeAlt}
          />
        </div>
      ) : (
        <LoadingSpinner className="loading-spinner--small-padding" />
      )}

      {status === 'pending' && (
        <div className="layout__content-container-bottom-centered">
          <Button
            disabled={isStartingSession}
            onClick={startSession}
            className="button--secondary button--proceed"
          >
            {isStartingSession && <LoadingSpinner inButton />}
            <span className="generic__button-text">{trans?.generateNewQrCodeBtn}</span>
          </Button>
        </div>
      )}
    </div>
  )
})
